<template>
  <BasePage :page-title="__DICT__.notifications.messages" :show-menu="true" :show-footer="false">
    <div class="container-xl py-3">
      <div v-if="!isAllowed" class="alert alert-info">
        {{ __DICT__.notifications.messagesNotAvailable }}
      </div>
      <div v-else>
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h2 class="h3 mb-1">{{ __DICT__.notifications.messages }}</h2>
            <!-- notifications-v2: the history is the server-side inbox (#141), not this device's -->
            <p v-if="notificationsV2" class="text-muted mb-0">
              {{ __DICT__.notifications.historyInInbox }}
              <RouterLink to="/inbox">{{ __DICT__.inbox.title }}</RouterLink>
            </p>
            <p v-else class="text-muted mb-0">{{ __DICT__.notifications.localHistory }}</p>
          </div>
          <button v-if="!notificationsV2" class="btn btn-outline-danger btn-sm" @click="clearAll" :disabled="messages.length === 0">
            {{ __DICT__.notifications.clearAll }}
          </button>
        </div>

        <!-- Push subscription controls -->
        <Card margin="mb-3">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <div class="fw-bold">{{ __DICT__.notifications.pushNotifications }}</div>
              <div class="small text-muted">
                <template v-if="!pushSupported">{{ __DICT__.notifications.notSupportedByBrowser }}</template>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <template v-else>
                  {{ __DICT__.notifications.permission }}: <strong>{{ permission }}</strong> · {{ __DICT__.notifications.subscribed }}: <strong>{{ isSubscribed ? __DICT__.yes : __DICT__.no }}</strong>
                </template>
              </div>
              <div v-if="errorMsg" class="text-danger small mt-2">{{ errorMsg }}</div>
              <div v-if="successMsg" class="text-success small mt-2">{{ successMsg }}</div>
            </div>
            <div class="ms-3">
              <button v-if="pushSupported && !isSubscribed" class="btn btn-primary" :disabled="busy || missingKey || iosNeedsInstall" @click="subscribeUser">
                <span v-if="busy" class="spinner-border spinner-border-sm me-2"></span>
                {{ missingKey ? __DICT__.notifications.missingKey : (iosNeedsInstall ? __DICT__.notifications.installAppFirst : __DICT__.notifications.subscribe) }}
              </button>
              <button v-else-if="pushSupported && isSubscribed" class="btn btn-outline-secondary" :disabled="busy" @click="unsubscribeUser">
                <span v-if="busy" class="spinner-border spinner-border-sm me-2"></span>
                {{ __DICT__.notifications.unsubscribe }}
              </button>
            </div>
          </div>
          <div v-if="missingKey" class="text-muted small mt-2">
            {{ __DICT__.notifications.vapidNotConfigured }}
          </div>
          <div v-else-if="iosNeedsInstall" class="text-muted small mt-2">
            {{ __DICT__.notifications.iosInstallInstructions }}
          </div>
        </Card>

        <template v-if="!notificationsV2">
        <Card v-if="messages.length === 0" margin="">
          <div class="text-center text-muted">
            {{ __DICT__.notifications.noMessages }}
          </div>
        </Card>

        <div v-else class="list-group">
          <div v-for="m in messages" :key="m.id" class="list-group-item">
            <div class="d-flex w-100 justify-content-between">
              <h3 class="h5 mb-1">{{ m.title || 'SnowFuze' }}</h3>
              <small class="text-muted">{{ formatTime(m.timestamp) }}</small>
            </div>
            <p class="mb-2">{{ m.body }}</p>
            <div class="d-flex align-items-center gap-2">
              <button class="btn btn-primary btn-sm" @click="openMessage(m)">{{ __DICT__.open }}</button>
              <button class="btn btn-outline-secondary btn-sm" @click="copyMessage(m)">{{ __DICT__.copy }}</button>
              <button class="btn btn-outline-danger btn-sm ms-auto" @click="deleteMessage(m.id)">{{ __DICT__.delete }}</button>
            </div>
          </div>
        </div>
        </template>

        <p class="text-muted small mt-3">{{ __DICT__.notifications.repId }}: {{ repId }}</p>
      </div>
    </div>
  </BasePage>
</template>

<script setup>
import BasePage from '@/components/BasePage.vue'
import { Card } from '@/components/atomic'
import { computed, ref, onMounted } from 'vue'
import { useWeekStore } from '@/stores/week.js'
import { useNotificationsStore } from '@/stores/notifications'
import brandConfig from '@brand/config.js'
import { api } from '@/services/api'
import { useConfigFlag } from '@/composables/useConfigFlag.js'
import { registerPushDevice, forgetPushDevice } from '@/services/pushDevice.js'
import { usePushReachabilityStore } from '@/stores/pushReachability.js'

const weekStore = useWeekStore()
const repId = computed(() => String(weekStore.repObj?.repId ?? ''))
// notifications-v2 opens the page to every staff member of the tenant; off, one rep only.
const { enabled: notificationsV2 } = useConfigFlag('notifications-v2')
const isAllowed = computed(() => notificationsV2.value || repId.value === '30942')

// Under notifications-v2 the push device is registered with its user agent and
// remembered for the re-sync on open; off, the page posts the subscription as before.
async function postPushDevice(subscription) {
  if (notificationsV2.value) {
    await registerPushDevice(subscription)
  } else {
    await api.post('/api/subscriptions/subscribe', subscription)
  }
}

const notificationsStore = useNotificationsStore()
// The banner and the "require push" block follow what is done here (#149).
const pushReachability = usePushReachabilityStore()
const messages = computed(() => notificationsStore.messagesSorted)

// Push subscription state
const pushSupported = ref(false)
const permission = ref(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported')
const isSubscribed = ref(false)
const busy = ref(false)
const errorMsg = ref('')
const successMsg = ref('')
const missingKey = computed(() => !(brandConfig?.pwa?.vapidPublicKey))
const debugInfo = ref({ keyLen: null, keyFirstByte: null, swState: 'unknown' })

// Environment detection (notably for iOS requirements)
const isIos = (() => {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
})()
const isSafari = (() => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isCriOS = /CriOS\//.test(ua)
  const isFxiOS = /FxiOS\//.test(ua)
  const isEdgiOS = /EdgiOS\//.test(ua)
  return navigator.vendor === 'Apple Computer, Inc.' && !isCriOS && !isFxiOS && !isEdgiOS
})()
const isStandalone = (() => {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true
})()
const iosNeedsInstall = computed(() => isIos && isSafari && !isStandalone)

function base64UrlToUint8Array(base64String) {
  const pad = '='.repeat((4 - (base64String.length % 4)) % 4)
  const b64 = (base64String.replace(/-/g, '+').replace(/_/g, '/')) + pad
  const raw = atob(b64)
  let outputArray = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; ++i) {
    outputArray[i] = raw.charCodeAt(i)
  }
  // Ensure uncompressed EC point format (0x04 || X || Y)
  if (outputArray.length === 64) {
    const arr = new Uint8Array(65)
    arr[0] = 0x04
    arr.set(outputArray.slice(0, 64), 1)
    outputArray = arr
  }
  return outputArray
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function refreshSubscriptionStatus() {
  permission.value = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    isSubscribed.value = !!subscription
    if (subscription && subscription.endpoint) {
      localStorage.setItem('pushSubscribed', '1')
      localStorage.setItem('pushEndpoint', subscription.endpoint)
    } else {
      localStorage.removeItem('pushSubscribed')
      localStorage.removeItem('pushEndpoint')
    }
  } catch (e) {
    // ignore
  }
}

onMounted(async () => {
  pushSupported.value = ('serviceWorker' in navigator) && ('PushManager' in window)
  await refreshSubscriptionStatus()
  // Compute debug info about provided VAPID key
  try {
    if (brandConfig?.pwa?.vapidPublicKey) {
      const arr = base64UrlToUint8Array(brandConfig.pwa.vapidPublicKey)
      debugInfo.value.keyLen = arr?.length || 0
      debugInfo.value.keyFirstByte = arr?.[0] ?? null
      console.log('[Push Debug] VAPID key bytes:', { len: debugInfo.value.keyLen, firstByte: debugInfo.value.keyFirstByte })
    } else {
      console.warn('[Push Debug] Missing VAPID public key in brand config')
    }
  } catch (e) {
    console.warn('[Push Debug] Failed to parse VAPID key', e)
  }
})

async function subscribeUser() {
  errorMsg.value = ''
  successMsg.value = ''
  if (!pushSupported.value || missingKey.value) return

  try {
    busy.value = true

    // Request permission
    if (Notification.permission !== 'granted') {
      const perm = await Notification.requestPermission()
      if (perm !== 'granted') {
        errorMsg.value = 'Notification permission was not granted.'
        return
      }
    }

    const registration = await navigator.serviceWorker.ready

    // ⭐ KEY FIX: Always unsubscribe first to clear any conflicts
    try {
      const existingSubscription = await registration.pushManager.getSubscription()
      if (existingSubscription) {
        console.log('[Push] Removing existing subscription...')
        await existingSubscription.unsubscribe()
        // Wait for push service to process the unsubscribe
        await new Promise(resolve => setTimeout(resolve, 500))
      }
    } catch (e) {
      console.warn('Failed to clear old subscription:', e)
    }

    const appServerKey = base64UrlToUint8Array(brandConfig.pwa.vapidPublicKey)
    console.log(appServerKey)

    // Validate key format
    if (!appServerKey || appServerKey.length !== 65 || appServerKey[0] !== 0x04) {
      errorMsg.value = `Invalid VAPID key format (length: ${appServerKey?.length})`
      return
    }

    // Now subscribe with clean state
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appServerKey
    })
    console.log('[Push] Subscribed:', subscription)

    // Send to backend
    await postPushDevice(subscription)

    localStorage.setItem('pushSubscribed', '1')
    localStorage.setItem('pushEndpoint', subscription.endpoint || '')
    successMsg.value = 'Successfully subscribed to push notifications.'
    await refreshSubscriptionStatus()
    pushReachability.refresh()

  } catch (e) {
    console.error('[Push Error]:', e)
    errorMsg.value = `Subscribe failed: ${e.message || 'Unknown error'}`
  } finally {
    busy.value = false
  }
}

async function subscribeUserOld() {
  errorMsg.value = ''
  successMsg.value = ''
  if (!pushSupported.value) return
  if (missingKey.value) {
    errorMsg.value = 'VAPID public key is not configured.'
    return
  }
  try {
    busy.value = true
    // Request permission if needed
    if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
      const perm = await Notification.requestPermission?.() || Notification.permission
      if (perm !== 'granted') {
        errorMsg.value = 'Notification permission was not granted.'
        await refreshSubscriptionStatus()
        return
      }
    }

    const registration = await navigator.serviceWorker.ready

    // Force an update check to avoid races with a just-updated SW
    try {
      await registration.update()
    } catch {}
    try {
      debugInfo.value.swState = registration?.active?.state || 'unknown'
    } catch {}
    if (registration?.active && registration.active.state !== 'activated') {
      await new Promise((resolve) => {
        try {
          registration.active.addEventListener('statechange', function onStateChange() {
            if (registration.active.state === 'activated') {
              registration.active.removeEventListener('statechange', onStateChange)
              resolve()
            }
          })
        } catch { resolve() }
      })
    }
    // Small delay to let SW settle
    await wait(200)

    // If a subscription already exists, reuse it to avoid browser AbortError/InvalidStateError
    let existing = null
    try { existing = await registration.pushManager.getSubscription() } catch {}
    if (existing) {
      try { await api.post('/api/subscriptions/subscribe', existing) } catch (e) { console.warn('Backend subscribe (reuse) failed', e) }
      localStorage.setItem('pushSubscribed', '1')
      localStorage.setItem('pushEndpoint', existing.endpoint || '')
      isSubscribed.value = true
      successMsg.value = 'Already subscribed. Details refreshed.'
      await refreshSubscriptionStatus()
      return
    }

    const appServerKey = base64UrlToUint8Array(brandConfig.pwa.vapidPublicKey)
    if (!appServerKey || appServerKey.length !== 65 || appServerKey[0] !== 0x04) {
      console.error('[Push Debug] Invalid VAPID key bytes', { len: appServerKey?.length, first: appServerKey?.[0] })
      errorMsg.value = `VAPID key appears invalid (len=${appServerKey?.length || 0}). Please contact support.`
      await refreshSubscriptionStatus()
      return
    }

    let subscription
    try {
      console.log('[Push Debug] Calling subscribe()')
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey
      })
    } catch (err) {
      // Handle common cases where the browser throws AbortError/InvalidStateError
      if (err && (err.name === 'AbortError' || err.name === 'InvalidStateError')) {
        // Re-check if a subscription actually exists despite the error
        try { existing = await registration.pushManager.getSubscription() } catch {}
        if (existing) {
          try { await api.post('/api/subscriptions/subscribe', existing) } catch (e) { console.warn('Backend subscribe after error failed', e) }
          localStorage.setItem('pushSubscribed', '1')
          localStorage.setItem('pushEndpoint', existing.endpoint || '')
          isSubscribed.value = true
          successMsg.value = 'Subscribed.'
          await refreshSubscriptionStatus()
          return
        }
        // Try to recover from potential VAPID key mismatch by clearing old sub then retry once
        try {
          const oldSub = await registration.pushManager.getSubscription()
          if (oldSub) { await oldSub.unsubscribe().catch(() => {}) }
        } catch {}
        try {
          subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appServerKey })
        } catch (err2) {
          console.error('Subscribe retry failed:', err2)
          // Final backoff and try once more
          await wait(400)
          try {
            subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appServerKey })
          } catch (err3) {
            console.error('Subscribe final retry failed:', err3)
            errorMsg.value = (err3 && err3.message) ? `Subscribe failed: ${err3.message}` : 'Failed to subscribe.'
            await refreshSubscriptionStatus()
            return
          }
        }
      } else {
        console.error(err)
        if (err && err.name === 'NotAllowedError') {
          errorMsg.value = 'Notifications are blocked. Please allow notifications in your browser settings.'
        } else if (typeof window !== 'undefined' && window.isSecureContext === false) {
          errorMsg.value = 'Secure context required (HTTPS). Please use https:// and try again.'
        } else {
          errorMsg.value = err?.message || 'Failed to subscribe.'
        }
        await refreshSubscriptionStatus()
        return
      }
    }

    // If we got here with a subscription, post to backend
    if (subscription) {
      try {
        await api.post('/api/subscriptions/subscribe', subscription)
      } catch (e) {
        // Even if backend call fails, keep the browser subscription and show error
        console.warn('Backend subscribe failed', e)
      }

      localStorage.setItem('pushSubscribed', '1')
      localStorage.setItem('pushEndpoint', subscription.endpoint || '')
      successMsg.value = 'Subscribed to push notifications.'
      await refreshSubscriptionStatus()
    }
  } catch (e) {
    console.error(e)
    errorMsg.value = e?.message || 'Failed to subscribe.'
  } finally {
    busy.value = false
  }
}

async function unsubscribeUser() {
  errorMsg.value = ''
  successMsg.value = ''
  try {
    busy.value = true
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
      successMsg.value = 'You are not subscribed.'
      isSubscribed.value = false
      localStorage.removeItem('pushSubscribed')
      localStorage.removeItem('pushEndpoint')
      return
    }

    // Inform backend first (best-effort)
    try {
      await api.post('/api/subscriptions/unsubscribe', { endpoint: subscription.endpoint })
    } catch (e) {
      console.warn('Backend unsubscribe failed', e)
    }

    try { await subscription.unsubscribe() } catch {}

    // Turned off on purpose: the re-sync on open must not subscribe it again.
    forgetPushDevice()
    localStorage.removeItem('pushSubscribed')
    localStorage.removeItem('pushEndpoint')
    successMsg.value = 'Unsubscribed from push notifications.'
    await refreshSubscriptionStatus()
    pushReachability.refresh()
  } catch (e) {
    console.error(e)
    errorMsg.value = e?.message || 'Failed to unsubscribe.'
  } finally {
    busy.value = false
  }
}

function deleteMessage(id) {
  notificationsStore.deleteMessage(id)
}
function clearAll() {
  notificationsStore.clearAll()
}
function openMessage(m) {
  // Opening is just informational here; the global modal shows on delivery.
  // We can re-show content as an alert using the browser alert as a minimal implementation.
  alert(`${m.title || 'SnowFuze'}\n\n${m.body}`)
}
async function copyMessage(m) {
  try {
    await navigator.clipboard.writeText(`${m.title || 'SnowFuze'}\n\n${m.body}`)
  } catch {}
}
function formatTime(ts) {
  if (!ts) return ''
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return ''
  }
}
</script>

<style scoped>
</style>
